import { Button } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { starterSpecToml } from '@/components/FormCreate/createForm';
import { CodeBlock } from '@/components/Ui/CodeBlock';
import { CopyButton } from '@/components/Ui/CopyButton';
import { LedgerRow } from '@/components/Ui/LedgerRow';
import { Section } from '@/components/Ui/Section';
import { SegmentedButtons } from '@/components/Ui/SegmentedButtons';
import { useUndoToast } from '@/components/Ui/Toast';
import { UiLink } from '@/components/Ui/UiLink';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import {
  buildSetupPrompt,
  captchaScript,
  captchaWidget,
  HOSTED_FRAMEWORKS,
  type HostedFramework,
  hasFields,
  hostedFormUrl,
  iframeSnippet,
  linkUrl,
  ownFormExample,
  type SiteMethod,
} from '@/lib/form-site';
import { listTime } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import classes from './FormOverview.module.css';
import { TestSubmission } from './TestSubmission';
import type { SectionProps } from './types';

const FRAMEWORK_LABELS: Record<HostedFramework, string> = {
  bulma: 'Bulma',
  bootstrap: 'Bootstrap',
  formshive: 'Formshive',
};

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className={classes.step}>
      <span className={classes.num} aria-hidden="true">
        {n}
      </span>
      <div className={classes.stack}>
        <strong>{title}</strong>
        {children}
      </div>
    </div>
  );
}

function OwnInstructions({ form, spec }: Pick<SectionProps, 'form' | 'spec'>) {
  const { t } = useTranslation();
  const captcha = form.check_challenge;
  const example = ownFormExample(form.id, captcha, spec, t);
  let n = 0;

  const copyAll = [
    ...(captcha
      ? [
          `1. ${t('form.site.own.captchaTitle')}`,
          t('form.site.own.captchaHead'),
          captchaScript(),
          t('form.site.own.captchaForm'),
          captchaWidget(form.id),
          '',
        ]
      : []),
    `${captcha ? 2 : 1}. ${t('form.site.own.pointTitle')}`,
    example,
    '',
    `${captcha ? 3 : 2}. ${t('form.site.own.afterTitle')}`,
    form.redirect_url
      ? t('form.site.own.afterThanks', { url: form.redirect_url })
      : t('form.site.own.afterNone'),
  ].join('\n');

  return (
    <div className={classes.stack}>
      {captcha && (
        <Step n={++n} title={t('form.site.own.captchaTitle')}>
          <span className={classes.sub}>{t('form.site.own.captchaHead')}</span>
          <CodeBlock code={captchaScript()} copy />
          <span className={classes.sub}>{t('form.site.own.captchaForm')}</span>
          <CodeBlock code={captchaWidget(form.id)} copy />
        </Step>
      )}
      <Step n={++n} title={t('form.site.own.pointTitle')}>
        <CodeBlock code={example} copy />
        <span className={classes.help}>
          {hasFields(spec) ? t('form.site.own.fieldsNote') : t('form.site.own.emailNote')}
        </span>
      </Step>
      <Step n={++n} title={t('form.site.own.afterTitle')}>
        {form.redirect_url ? (
          <span className={classes.sub}>
            {t('form.site.own.afterThanks', { url: form.redirect_url })}
          </span>
        ) : (
          <span className={classes.sub}>
            {t('form.site.own.afterNone')}{' '}
            <UiLink to="#thanks">{t('form.site.own.setThanks')}</UiLink>
          </span>
        )}
      </Step>
      <div>
        <CopyButton text={copyAll} label={t('form.site.own.copyAll')} />
      </div>
    </div>
  );
}

function NeedsFields({
  form,
  method,
  onAdded,
}: {
  form: SectionProps['form'];
  method: SiteMethod;
  onAdded: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const { createLanguageURL } = useLanguageAwareRouting();
  const notify = useUndoToast();
  const [busy, setBusy] = useState(false);

  const addStarter = async () => {
    setBusy(true);
    try {
      await useRustyState
        .getState()
        .api.updateFormPartial(form.id, { specs: starterSpecToml('contact'), check_specs: true });
      await onAdded();
      notify(t('form.site.needsFields.added'));
    } catch {
      // the API layer reports the error
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classes.card}>
      <strong>{t(`form.site.needsFields.title.${method}`)}</strong>
      <span className={classes.sub}>{t('form.site.needsFields.body')}</span>
      <div className={classes.row}>
        <Button variant="filled" size="xs" onClick={() => void addStarter()} loading={busy}>
          {t('form.site.needsFields.addStarter')}
        </Button>
        <Button
          component={Link}
          size="xs"
          to={createLanguageURL(`/account/forms/${form.id}/fields`)}
        >
          {t('form.site.needsFields.editor')}
        </Button>
      </div>
    </div>
  );
}

function HostedInstructions({
  form,
  framework,
}: Pick<SectionProps, 'form'> & { framework: HostedFramework }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(false);
  const snippet = iframeSnippet(form.id, framework, form.title);
  return (
    <div className={classes.stack}>
      <span className={classes.sub}>{t('form.site.hosted.paste')}</span>
      <CodeBlock code={snippet} wrap />
      <div className={classes.row}>
        <CopyButton text={snippet} label={t('form.site.hosted.copy')} />
        <Button size="xs" aria-expanded={preview} onClick={() => setPreview(!preview)}>
          {preview ? t('form.site.hosted.hidePreview') : t('form.site.hosted.preview')}
        </Button>
      </div>
      {preview && (
        <div className={classes.preview}>
          <iframe
            src={`${hostedFormUrl(form.id, framework)}&track=false`}
            title={t('form.site.hosted.previewTitle', { title: form.title })}
          />
        </div>
      )}
    </div>
  );
}

function LinkInstructions({
  form,
  framework,
}: Pick<SectionProps, 'form'> & { framework: HostedFramework }) {
  const { t } = useTranslation();
  const url = linkUrl(form.id, framework, form.title);
  return (
    <div className={classes.stack}>
      <span className={classes.sub}>{t('form.site.link.share')}</span>
      <code className={classes.mono}>{url}</code>
      <div className={classes.row}>
        <CopyButton text={url} label={t('form.site.link.copy')} />
        <Button component="a" size="xs" href={url} target="_blank" rel="noopener noreferrer">
          {t('form.site.link.open')}
        </Button>
      </div>
      <span className={classes.help}>{t('form.site.link.titleNote')}</span>
    </div>
  );
}

function SetupPrompt({ prompt }: { prompt: string }) {
  const { t } = useTranslation();
  const [full, setFull] = useState(false);
  return (
    <div className={classes.card}>
      <div className={classes.promptHead}>
        <div className={classes.stack}>
          <strong>{t('form.site.prompt.title')}</strong>
          <span className={classes.help}>{t('form.site.prompt.help')}</span>
        </div>
        <CopyButton text={prompt} label={t('form.site.prompt.copy')} />
      </div>
      <pre className={`${classes.prompt}${full ? '' : ` ${classes.promptClamped}`}`}>{prompt}</pre>
      <div>
        <Button variant="subtle" size="xs" aria-expanded={full} onClick={() => setFull(!full)}>
          {full ? t('form.site.prompt.less') : t('form.site.prompt.more')}
        </Button>
      </div>
    </div>
  );
}

export function SiteSection(props: SectionProps) {
  const { form, save, data, spec, method, hasMessages } = props;
  const { t, i18n } = useTranslation();
  const [override, setOverride] = useState<boolean | null>(null);
  const [framework, setFramework] = useState<HostedFramework>('bulma');
  const expanded = override ?? !hasMessages;
  const firstRecipient = data.recipients?.find((r) => r.is_verified)?.email ?? null;
  const fields = hasFields(spec);
  const lastMessage = data.summary?.last_message_at;

  const prompt = buildSetupPrompt(
    {
      formId: form.id,
      title: form.title,
      method,
      captcha: form.check_challenge,
      spec,
      redirectUrl: form.redirect_url,
      allowedOrigins: form.allowed_origins ?? [],
      firstRecipient,
      framework,
    },
    t
  );

  const setMethod = (next: SiteMethod) =>
    void save(
      { method: next },
      t('form.site.methodSaved', { method: t(`form.site.method.${next}`) })
    );

  const methodHelp = [
    t(`form.site.methodHelp.${method}`),
    method !== 'own' && form.check_challenge ? t('form.site.captchaIncluded') : '',
    lastMessage ? t('form.site.lastMessage', { when: listTime(lastMessage, i18n.language) }) : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Section
      id="site"
      title={t('form.site.title')}
      subtitle={hasMessages ? undefined : t('form.site.subtitle')}
    >
      {hasMessages && (
        <LedgerRow
          label={t('form.site.methodLabel')}
          value={t(`form.site.method.${method}`)}
          help={methodHelp}
          action={
            <Button size="xs" aria-expanded={expanded} onClick={() => setOverride(!expanded)}>
              {expanded ? t('form.site.hideInstructions') : t('form.site.showInstructions')}
            </Button>
          }
        />
      )}
      {expanded && (
        <div className={classes.block}>
          <div className={classes.methodLine}>
            <span className={classes.help}>{t('form.site.methodLabel')}</span>
            <SegmentedButtons
              aria-label={t('form.site.methodLabel')}
              options={(['own', 'hosted', 'link'] as const).map((m) => ({
                value: m,
                label: t(`form.site.method.${m}`),
              }))}
              value={method}
              onChange={setMethod}
            />
          </div>
          {method !== 'own' && fields && (
            <div className={classes.methodLine}>
              <span className={classes.help}>{t('form.site.style')}</span>
              <SegmentedButtons
                aria-label={t('form.site.style')}
                options={HOSTED_FRAMEWORKS.map((f) => ({ value: f, label: FRAMEWORK_LABELS[f] }))}
                value={framework}
                onChange={setFramework}
              />
            </div>
          )}
          {method === 'own' && <OwnInstructions form={form} spec={spec} />}
          {method !== 'own' && !fields && (
            <NeedsFields
              form={form}
              method={method}
              onAdded={async () => {
                await Promise.all([data.reloadForm(), data.reloadStatus()]);
              }}
            />
          )}
          {method === 'hosted' && fields && (
            <HostedInstructions form={form} framework={framework} />
          )}
          {method === 'link' && fields && <LinkInstructions form={form} framework={framework} />}
          <SetupPrompt prompt={prompt} />
          <TestSubmission form={form} spec={spec} data={data} email={firstRecipient} />
        </div>
      )}
    </Section>
  );
}
